import React, { useContext, useEffect, useState } from 'react';
import { BiCheck, BiPlus, BiPlay, BiX, BiCalendarEdit, BiErrorCircle, BiDownload, BiPaperclip } from 'react-icons/bi';
import apiUser from '../../../../apiUser';
import { UserContext } from '../../../../contexts/userProvider';
import LoadingSpinner from '../../../Loader/load';
import AtivarContrato, { botaoAtivarCls } from './AtivarContrato';
import { CORES_EVENTO, brl, montarTimeline } from './eventosTimeline';

// Eventos do contrato em TIMELINE (07/out/2026). A ativação (Homologação da
// Instalação) não passa mais por aqui como tipo: é o modal AtivarContrato.

const fmtBRL = (v) => (v == null ? '—' : brl(v));
const TIPOS = [
    { valor: 'reajuste', label: 'Reajuste', dica: 'Percentual sobre o valor', novo: 'Novo reajuste' },
    { valor: 'aditivo', label: 'Aditivo', dica: 'Acrescenta valor ou itens', novo: 'Novo aditivo' },
    { valor: 'supressao', label: 'Supressão', dica: 'Reduz valor ou itens', novo: 'Nova supressão' },
    { valor: 'renovacao', label: 'Renovação', dica: 'Prorroga a vigência (com ou sem reajuste)', novo: 'Nova renovação' },
    { valor: 'cancelamento', label: 'Cancelamento', dica: 'Encerra o contrato e as cobranças a partir de uma data', novo: 'Cancelar contrato' },
];
// Reverter cancelamento (spec 2026-10-27): só administrador, fora da grade de tipos.
const TIPO_REVERSAO = { valor: 'reativacao', label: 'Reverter cancelamento', novo: 'Reverter cancelamento' };
// Renovação com reajuste (09/out/2026): prazo + percentual num lançamento só.
const MODOS_POR_TIPO = {
    reajuste: ['percentual'],
    aditivo: ['percentual', 'valor', 'itens'],
    supressao: ['percentual', 'valor', 'itens'],
    renovacao: ['vigencia', 'percentual'],
    cancelamento: ['cancelamento'],
    reativacao: ['reversao'],
};
const LABEL_MODO = { percentual: 'Percentual (%)', valor: 'Valor (R$)', itens: 'Itens', vigencia: 'Vigência' };
const rotuloModo = (tipo, m) => (tipo === 'renovacao'
    ? { vigencia: 'Só prazo', percentual: 'Prazo + reajuste (%)' }[m]
    : LABEL_MODO[m]);
// Reajuste com "renova a vigência até" preenchido vira renovação com reajuste.
const tipoDoLancamento = (f) => (f.tipo === 'reajuste' && f.data_fim_nova ? 'renovacao' : f.tipo);

const agoraLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16); // datetime-local
};

const formVazio = (tipo) => {
    const agora = agoraLocal();
    return {
        tipo, modo: MODOS_POR_TIPO[tipo][0], percentual: '', valor: '',
        data_evento: agora, data_evento_inicial: agora, motivo: '', data_fim_nova: '',
        itens_novos: [], itens_alterados: [], numero_termo: '', arquivo: null,
    };
};
// Item novo do aditivo (09/out/2026): a implantada é a digitada, padrão 0 — o item
// entra no faturamento só com o que já está instalado.
const itemNovoVazio = () => ({ descricao: '', quantidade_contratada: 1, quantidade_implantada: 0, valor_unitario: '', unidade: 'Mês', tipo: 'Serviço' });
// "Lançar aditivo de itens" vindo do Editar itens: abre já em Aditivo › Itens com a sugestão.
export const formAditivoItens = (itensNovos) => ({
    ...formVazio('aditivo'), modo: 'itens',
    itens_novos: (itensNovos && itensNovos.length ? itensNovos : [itemNovoVazio()]).map((n) => ({ ...itemNovoVazio(), ...n })),
});

// Formulário com algo digitado: o modal não fecha por Esc nem por clique fora
// (só pelos botões Fechar/Cancelar).
const formTemDados = (f) => Boolean(f) && Boolean(
    String(f.percentual).trim() || String(f.valor).trim() || f.motivo.trim() || f.data_fim_nova
    || String(f.numero_termo || '').trim() || f.arquivo || f.itens_novos.length || f.itens_alterados.length || f.data_evento !== f.data_evento_inicial
);

const inputCls = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-gray-100 focus:border-vox-blue-500 focus:ring-2 focus:ring-vox-blue-500';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const focoCls = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vox-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800';
const ddmm = (ymd) => `${String(ymd).slice(8, 10)}/${String(ymd).slice(5, 7)}`;

// Bolinha da timeline: ativação vigente leva o check; cadastro e ativação
// substituída são vazadas; o passo pendente (implantação) é tracejado.
const Ponto = ({ item, comBotao }) => {
    if (item.pendente) {
        // centralizada no cartão do aviso (mais alto quando tem o botão Ativar)
        return <span className={`relative h-5 w-5 rounded-full border-2 border-dashed border-vox-green-500 bg-white dark:bg-gray-800 ${comBotao ? 'mt-5' : 'mt-3'}`} />;
    }
    if (item.ehAtivacao) {
        return (
            <span className={`relative mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white ring-4 ${item.cor.ponto} ${item.cor.anel}`}>
                <BiCheck className='h-4 w-4' aria-hidden='true' />
            </span>
        );
    }
    if (item.tipo === 'cadastro' || item.substituida) {
        return <span className={`relative mt-1 h-3.5 w-3.5 rounded-full border-2 bg-white dark:bg-gray-800 ${item.substituida ? 'border-sky-300 dark:border-sky-700' : 'border-gray-400 dark:border-gray-500'}`} />;
    }
    return <span className={`relative mt-1 h-3.5 w-3.5 rounded-full ring-4 ${item.cor.ponto} ${item.cor.anel}`} />;
};

const EventosContrato = ({ contrato, client, onClose, onMudou, aditivoInicial }) => {
    const { isGerente, isAdmin } = useContext(UserContext) || {};
    const [dataCancelamento, setDataCancelamento] = useState(contrato.data_cancelamento || null);
    const [eventos, setEventos] = useState(null);
    const [podeCorrigir, setPodeCorrigir] = useState(false);
    const [status, setStatus] = useState(contrato.status);
    const [itensContrato, setItensContrato] = useState([]);
    // null = fechado; senão o formulário do tipo escolhido (aditivoInicial: vem do Editar itens).
    const [form, setForm] = useState(() => (aditivoInicial ? formAditivoItens(aditivoInicial) : null));
    const [lancando, setLancando] = useState(false);
    const [erro, setErro] = useState('');
    const [previa, setPrevia] = useState(null); // prévia do proporcional (24/set)
    const [ativar, setAtivar] = useState(null); // null | { modo, inicial }

    useEffect(() => { setStatus(contrato.status); }, [contrato.status]);
    useEffect(() => { setDataCancelamento(contrato.data_cancelamento || null); }, [contrato.data_cancelamento]);

    const carregar = () => {
        apiUser.get(`/contratos/${contrato.id}/eventos`)
            .then(({ data }) => {
                setEventos(data.success ? data.dados : []);
                setPodeCorrigir(Boolean(data.success && data.pode_corrigir_ativacao));
            })
            .catch(() => { setEventos([]); setPodeCorrigir(false); });
    };
    useEffect(() => {
        carregar();
        apiUser.post('/itens/dados', { id_cliente: client.id })
            .then(({ data }) => data.success &&
                setItensContrato(data.dados.filter((i) => Number(i.id_contrato) === Number(contrato.id))))
            .catch(() => setItensContrato([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contrato.id]);

    const comDados = formTemDados(form);
    const fecharSePuder = () => { if (!ativar && !lancando && !comDados) onClose(); };
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && !ativar && !lancando && !comDados) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [ativar, lancando, comDados, onClose]);

    const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
    const abrirTipo = (tipo) => {
        setErro('');
        setPrevia(null);
        setForm((f) => (f && f.tipo === tipo ? null : formVazio(tipo)));
    };

    // Monta o payload comum (usado pela prévia e pelo lançamento).
    const montarPayload = () => {
        const tipo = tipoDoLancamento(form);
        const payload = {
            id_contrato: contrato.id, tipo, modo: form.modo, motivo: form.motivo,
            data_evento: form.data_evento ? form.data_evento.replace('T', ' ') + ':00' : undefined,
        };
        if (String(form.numero_termo || '').trim()) payload.numero_termo = String(form.numero_termo).trim();
        if (form.modo === 'percentual') {
            let p = Number(String(form.percentual).replace(',', '.'));
            if (form.tipo === 'supressao') p = -Math.abs(p);
            payload.percentual = p;
        }
        if (form.modo === 'valor') payload.valor = Number(String(form.valor).replace(',', '.'));
        if (tipo === 'renovacao') payload.data_fim_nova = form.data_fim_nova;
        if (form.modo === 'itens') {
            payload.itens_novos = form.itens_novos.filter((n) => n.descricao);
            payload.itens_alterados = form.itens_alterados
                .filter((a) => a.id && Number(a.delta_quantidade))
                .map((a) => ({
                    id: Number(a.id),
                    delta_quantidade: form.tipo === 'supressao'
                        ? -Math.abs(Number(a.delta_quantidade))
                        : Math.abs(Number(a.delta_quantidade)),
                }));
        }
        return payload;
    };

    // Prévia ao vivo do proporcional: recalcula (com debounce) ao mudar o form.
    useEffect(() => {
        if (!form || form.modo === 'vigencia' || form.modo === 'reversao') { setPrevia(null); return undefined; }
        const pronto = (form.modo === 'cancelamento' && form.data_evento)
            || (form.modo === 'percentual' && Number(String(form.percentual).replace(',', '.')))
            || (form.modo === 'valor' && Number(String(form.valor).replace(',', '.')))
            || (form.modo === 'itens' && (form.itens_novos.some((n) => n.descricao) || form.itens_alterados.some((a) => a.id)));
        if (!pronto) { setPrevia(null); return undefined; }
        // `vivo`: resposta atrasada de um tipo/form anterior não cai no form atual.
        let vivo = true;
        const t = setTimeout(() => {
            apiUser.post('/contratos/eventos/preview', montarPayload())
                .then(({ data }) => { if (vivo) setPrevia(data.success ? data.dados : null); })
                .catch(() => { if (vivo) setPrevia(null); });
        }, 400);
        return () => { vivo = false; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form?.tipo, form?.modo, form?.percentual, form?.valor, form?.data_evento, form?.data_fim_nova, form?.itens_novos, form?.itens_alterados]);

    // Anexo do termo (PDF) de um evento já lançado.
    const anexarTermo = async (idEvento, arquivo) => {
        const fd = new FormData();
        fd.append('arquivo', arquivo);
        try {
            const { data } = await apiUser.post(`/contratos/eventos/${idEvento}/anexo`, fd);
            return data;
        } catch (e) {
            return { success: false, message: 'sem resposta do servidor' };
        }
    };
    const anexarPelaTimeline = async (idEvento, e) => {
        const arquivo = e.target.files[0];
        e.target.value = '';
        if (!arquivo) return;
        const r = await anexarTermo(idEvento, arquivo);
        if (!r.success) alert(`O termo não foi anexado: ${r.message}`);
        carregar();
    };
    const baixarTermo = async (item) => {
        try {
            const resp = await apiUser.get(`/contratos/eventos/${item.id}/anexo`, { responseType: 'blob' });
            if (resp.data && resp.data.type === 'application/json') {
                const txt = JSON.parse(await resp.data.text());
                return alert(txt.message);
            }
            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.anexoNome || 'termo.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) { alert('Não foi possível baixar o termo.'); }
    };

    const lancar = async () => {
        setLancando(true);
        setErro('');
        try {
            const arquivo = form.arquivo;
            const payload = montarPayload();
            const { data } = await apiUser.post('/contratos/eventos/lancar', payload);
            if (data.success) {
                const comResumo = (data.dados && Array.isArray(data.dados.ciclos_retroativos) && data.dados.ciclos_retroativos.length)
                    || ['cancelamento', 'reativacao'].includes(payload.tipo)
                    || (data.dados && Array.isArray(data.dados.ciclos_desatualizados) && data.dados.ciclos_desatualizados.length);
                if (comResumo) alert(data.message);
                if (payload.tipo === 'cancelamento') {
                    setStatus('encerrado');
                    setDataCancelamento(String(payload.data_evento).slice(0, 10));
                }
                if (payload.tipo === 'reativacao') {
                    let volta = 'ativo';
                    try { volta = JSON.parse(data.dados.detalhe).status.para || 'ativo'; } catch (e) { /* ativo */ }
                    setStatus(volta);
                    setDataCancelamento(null);
                }
                // O evento já foi gravado: falha no anexo só avisa (dá para anexar pela timeline).
                if (arquivo && data.dados && data.dados.id) {
                    const up = await anexarTermo(data.dados.id, arquivo);
                    if (!up.success) alert(`Evento lançado, mas o termo não foi anexado: ${up.message}. Anexe pela timeline.`);
                }
                setForm(null);
                setPrevia(null);
                carregar();
                onMudou && onMudou({ tipo: payload.tipo, modo: payload.modo });
            } else {
                setErro(data.message || 'O evento não foi lançado. Confira os campos.');
            }
        } catch (e) {
            setErro('Sem resposta do servidor. O evento não foi lançado; tente de novo.');
        } finally { setLancando(false); }
    };

    const aoAtivar = () => {
        setAtivar(null);
        setStatus('ativo');
        carregar();
        onMudou && onMudou();
    };

    const timeline = eventos === null ? null : montarTimeline(eventos, { ...contrato, status });
    const itens = timeline && status === 'implantacao'
        ? [{ chave: 'pendente', pendente: true, fase: 'implantacao' }, ...timeline]
        : timeline;
    // Legenda do trilho: só o que aparece (trecho abaixo de cada item = fase do item seguinte).
    const temTrilhoContinuo = itens && itens.slice(1).some((i) => i.fase === 'faturando');
    const temTrilhoTracejado = itens && itens.slice(1).some((i) => i.fase === 'implantacao');
    const tipoForm = form && (form.tipo === TIPO_REVERSAO.valor ? TIPO_REVERSAO : TIPOS.find((t) => t.valor === form.tipo));
    const corForm = form && CORES_EVENTO[form.tipo];

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
            <div className='fixed inset-0 bg-black/50' onClick={fecharSePuder} />
            <div role='dialog' aria-modal='true' aria-labelledby='eventos-contrato-titulo'
                className='relative flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white dark:bg-gray-800 shadow-2xl'>
                <header className='flex items-start justify-between gap-4 border-b border-gray-200 dark:border-gray-700 px-6 py-4'>
                    <div className='min-w-0'>
                        <h2 id='eventos-contrato-titulo' className='text-xl font-bold text-vox-blue-900 dark:text-white'>Eventos do contrato</h2>
                        <p className='mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400'>{contrato.numero ? `${contrato.numero} — ` : ''}{contrato.nome}</p>
                    </div>
                    <button type='button' onClick={onClose} aria-label='Fechar'
                        className={`-mr-2 rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200 ${focoCls}`}>
                        <BiX className='h-6 w-6' />
                    </button>
                </header>

                <div className='flex-1 min-h-0 space-y-6 overflow-auto px-6 py-5'>
                    {status === 'ativo' && isGerente && (
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5' role='group' aria-label='Lançar evento'>
                            {TIPOS.map((t) => {
                                const ativo = form && form.tipo === t.valor;
                                const cor = CORES_EVENTO[t.valor];
                                return (
                                    <button key={t.valor} type='button' aria-pressed={Boolean(ativo)} onClick={() => abrirTipo(t.valor)}
                                        className={`flex flex-col items-start rounded-lg border px-3.5 py-3 text-left transition-colors duration-150 ${focoCls} ${ativo ? cor.botaoAtivo : cor.botao}`}>
                                        <span className='flex items-center gap-1 text-sm font-semibold'>
                                            <BiPlus className='h-4 w-4' aria-hidden='true' />{t.label}
                                        </span>
                                        <span className={`mt-0.5 text-xs ${ativo ? 'text-white/85' : 'opacity-75'}`}>{t.dica}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {status === 'encerrado' && dataCancelamento && !form && (
                        <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3 dark:border-zinc-600 dark:bg-zinc-800/60'>
                            <p className='text-sm font-medium text-zinc-800 dark:text-zinc-100'>
                                Contrato cancelado — sem cobrança a partir de {String(dataCancelamento).slice(0, 10).split('-').reverse().join('/')}.
                            </p>
                            {isAdmin && (
                                <button type='button' onClick={() => { setErro(''); setForm(formVazio('reativacao')); }}
                                    className={`rounded-lg border border-teal-600 px-3 py-1.5 text-sm font-semibold text-teal-700 hover:bg-teal-50 dark:border-teal-500 dark:text-teal-300 dark:hover:bg-teal-900/30 ${focoCls}`}>
                                    Reverter cancelamento
                                </button>
                            )}
                        </div>
                    )}

                    {form && (
                        <section aria-label={tipoForm.novo}
                            className='relative space-y-4 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 pl-5'>
                            <div className='flex flex-wrap items-center justify-between gap-3'>
                                <h3 className={`text-base font-semibold ${corForm.texto}`}>{tipoForm.novo}</h3>
                                {MODOS_POR_TIPO[form.tipo].length > 1 && (
                                    <div role='group' aria-label='Modo' className='inline-flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-0.5'>
                                        {MODOS_POR_TIPO[form.tipo].map((m) => (
                                            <button key={m} type='button' aria-pressed={form.modo === m}
                                                onClick={() => setForm((f) => ({ ...f, modo: m }))}
                                                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${focoCls} ${form.modo === m
                                                    ? 'bg-vox-blue-900 text-white dark:bg-vox-blue-600'
                                                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'}`}>
                                                {rotuloModo(form.tipo, m)}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                                <div>
                                    <label htmlFor='evento-data' className={labelCls}>{form.tipo === 'cancelamento'
                                        ? <>Data do cancelamento <span className='text-red-600'>*</span></>
                                        : (form.tipo === 'renovacao' && form.modo === 'vigencia') || form.tipo === 'reativacao' ? 'Data do evento' : 'Início da cobrança'}</label>
                                    <input id='evento-data' type='datetime-local' className={inputCls} value={form.data_evento}
                                        onChange={(e) => set('data_evento', e.target.value)} />
                                    {form.tipo === 'cancelamento' && (
                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Primeiro dia SEM cobrança: o dia do cancelamento não é cobrado.</p>
                                    )}
                                </div>
                                {form.modo === 'percentual' && (
                                    <div>
                                        <label htmlFor='evento-percentual' className={labelCls}>Percentual (%){form.tipo === 'supressao' ? ' — aplicado como redução' : ''}</label>
                                        <input id='evento-percentual' type='number' step='0.01' className={inputCls} value={form.percentual}
                                            onChange={(e) => set('percentual', e.target.value)} placeholder='ex.: 8.5' />
                                    </div>
                                )}
                                {form.modo === 'valor' && (
                                    <div>
                                        <label htmlFor='evento-valor' className={labelCls}>Valor (R$){form.tipo === 'supressao' ? ' — subtraído do Valor Global' : ''}</label>
                                        <input id='evento-valor' type='number' step='0.01' min='0' className={inputCls} value={form.valor}
                                            onChange={(e) => set('valor', e.target.value)} placeholder='0,00' />
                                        {form.tipo === 'aditivo' && contrato.tipo !== 'global' && (
                                            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Em contrato por itens, cria um item novo "Aditivo — motivo" com esse valor.</p>
                                        )}
                                    </div>
                                )}
                                {(form.tipo === 'renovacao' || form.tipo === 'reajuste') && (
                                    <div>
                                        <label htmlFor='evento-data-fim' className={labelCls}>
                                            {form.tipo === 'reajuste' ? 'Renova a vigência até (opcional)' : <>Nova data fim <span className='text-red-600'>*</span></>}
                                        </label>
                                        <input id='evento-data-fim' type='date' className={inputCls} value={form.data_fim_nova}
                                            onChange={(e) => set('data_fim_nova', e.target.value)} />
                                        {form.tipo === 'reajuste' && (
                                            <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                                {form.data_fim_nova
                                                    ? 'Será lançado como Renovação com reajuste: aplica o percentual e atualiza a data fim do contrato.'
                                                    : 'Termo de prazo e valor? Preencha para renovar a vigência no mesmo lançamento.'}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                                <div>
                                    <label htmlFor='evento-termo' className={labelCls}>Nº do termo</label>
                                    <input id='evento-termo' type='text' maxLength={50} className={inputCls} value={form.numero_termo}
                                        onChange={(e) => set('numero_termo', e.target.value)} placeholder='Ex.: 2º Termo Aditivo 15/2026' />
                                </div>
                                <div>
                                    <label htmlFor='evento-anexo' className={labelCls}>Anexo do termo (PDF)</label>
                                    <input id='evento-anexo' type='file' accept='application/pdf'
                                        className='block w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border-0 file:bg-vox-blue-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-vox-blue-800 hover:file:bg-vox-blue-100 dark:text-gray-300 dark:file:bg-vox-blue-900/40 dark:file:text-vox-blue-100'
                                        onChange={(e) => set('arquivo', e.target.files[0] || null)} />
                                </div>
                            </div>

                            {form.modo === 'itens' && (
                                <div className='space-y-3'>
                                    {form.tipo === 'aditivo' && (
                                        <div>
                                            <div className='flex items-center justify-between'>
                                                <span className={labelCls}>Itens novos</span>
                                                <button type='button' className='text-sm font-semibold text-vox-blue-700 hover:underline dark:text-vox-blue-300'
                                                    onClick={() => set('itens_novos', [...form.itens_novos, itemNovoVazio()])}>
                                                    + adicionar
                                                </button>
                                            </div>
                                            {form.itens_novos.map((n, i) => (
                                                <div key={i} className='mt-1 grid grid-cols-1 gap-2 md:grid-cols-5'>
                                                    <input className={`${inputCls} md:col-span-2`} placeholder='Descrição' aria-label={`Descrição do item novo ${i + 1}`} value={n.descricao}
                                                        onChange={(e) => set('itens_novos', form.itens_novos.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
                                                    <input type='number' min='1' className={inputCls} placeholder='Qtd. contratada' aria-label={`Quantidade contratada do item novo ${i + 1}`} value={n.quantidade_contratada}
                                                        onChange={(e) => set('itens_novos', form.itens_novos.map((x, j) => j === i ? { ...x, quantidade_contratada: e.target.value } : x))} />
                                                    <input type='number' min='0' step='1' className={inputCls} placeholder='Qtd. implantada' aria-label={`Quantidade implantada do item novo ${i + 1}`} value={n.quantidade_implantada}
                                                        onChange={(e) => set('itens_novos', form.itens_novos.map((x, j) => j === i ? { ...x, quantidade_implantada: e.target.value } : x))} />
                                                    <input type='number' step='0.01' className={inputCls} placeholder='Valor unitário' aria-label={`Valor unitário do item novo ${i + 1}`} value={n.valor_unitario}
                                                        onChange={(e) => set('itens_novos', form.itens_novos.map((x, j) => j === i ? { ...x, valor_unitario: e.target.value } : x))} />
                                                </div>
                                            ))}
                                            {form.itens_novos.length > 0 && (
                                                <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                                                    Qtd. implantada: o que já está instalado (padrão 0). Só ela entra na fatura; ajuste depois pela edição rápida de quantidade.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        <div className='flex items-center justify-between'>
                                            <span className={labelCls}>{form.tipo === 'supressao' ? 'Reduzir quantidade de itens' : 'Aumentar quantidade de itens'}</span>
                                            <button type='button' className='text-sm font-semibold text-vox-blue-700 hover:underline dark:text-vox-blue-300'
                                                onClick={() => set('itens_alterados', [...form.itens_alterados, { id: '', delta_quantidade: 1 }])}>
                                                + adicionar
                                            </button>
                                        </div>
                                        {form.itens_alterados.map((a, i) => (
                                            <div key={i} className='mt-1 grid grid-cols-1 gap-2 md:grid-cols-3'>
                                                <select className={`${inputCls} md:col-span-2`} value={a.id}
                                                    onChange={(e) => set('itens_alterados', form.itens_alterados.map((x, j) => j === i ? { ...x, id: e.target.value } : x))}>
                                                    <option value=''>Selecione o item</option>
                                                    {itensContrato.map((it) => (
                                                        <option key={it.id} value={it.id}>{it.descricao} (contratada: {it.quantidade_contratada})</option>
                                                    ))}
                                                </select>
                                                <input type='number' min='1' className={inputCls} placeholder='Quantidade' value={a.delta_quantidade}
                                                    onChange={(e) => set('itens_alterados', form.itens_alterados.map((x, j) => j === i ? { ...x, delta_quantidade: e.target.value } : x))} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {form.tipo === 'cancelamento' && (
                                <p className='rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'>
                                    Ciclos a partir desta data serão cancelados. O que já foi aprovado ou faturado não muda no ERP: vira pendência de estorno no Faturamento para resolver no Elleven.
                                </p>
                            )}
                            {form.tipo === 'reativacao' && (
                                <p className='rounded-lg border border-teal-300 bg-teal-50 px-3 py-2.5 text-sm text-teal-900 dark:border-teal-700 dark:bg-teal-950/40 dark:text-teal-100'>
                                    O contrato volta ao status de antes do cancelamento, os ciclos cancelados voltam e as pendências de estorno abertas são descartadas.
                                </p>
                            )}
                            {previa && previa.cancelamento && (
                                <div className='space-y-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-sm text-gray-700 dark:text-gray-300'>
                                    <p className='font-semibold text-gray-900 dark:text-white'>Último faturamento</p>
                                    <p className='tabular-nums'>
                                        Ciclo {ddmm(previa.ciclo.inicio)} a {ddmm(previa.ciclo.fim)}: <b>{previa.dias_cobrados} de {previa.ciclo.dias} dias</b> cobrados
                                        {previa.dias_cobrados > 0 ? <> (até {ddmm(new Date(Date.parse(previa.inicio_cobranca + 'T12:00:00Z') - 86400000).toISOString().slice(0, 10))})</> : ' — o último ciclo cobrado é o anterior'}.
                                    </p>
                                    <p className='tabular-nums'>
                                        Mensal {fmtBRL(previa.valor_antes)} · <b>proporcional do último mês: {fmtBRL(previa.proporcional)}</b> (fora itens de cobrança única).
                                    </p>
                                </div>
                            )}
                            {previa && !previa.cancelamento && (
                                <div className='space-y-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-sm text-gray-700 dark:text-gray-300'>
                                    <p className='font-semibold text-gray-900 dark:text-white'>Cálculo proporcional</p>
                                    <p className='tabular-nums'>
                                        Início da cobrança em <b>{ddmm(previa.inicio_cobranca)}</b> — ciclo {ddmm(previa.ciclo.inicio)} a {ddmm(previa.ciclo.fim)}:
                                        {' '}<b>{previa.dias_cobrados} de {previa.ciclo.dias} dias</b>.
                                    </p>
                                    <p className='tabular-nums'>
                                        Efeito na fatura: <b>{fmtBRL(previa.delta)}</b> ({fmtBRL(previa.valor_antes)} → {fmtBRL(previa.valor_depois)}) ·
                                        {' '}<b>Proporcional no primeiro faturamento: {fmtBRL(previa.proporcional)}</b> · valor cheio a partir do ciclo seguinte.
                                    </p>
                                </div>
                            )}

                            <div>
                                <label htmlFor='evento-motivo' className={labelCls}>Motivo <span className='text-red-600'>*</span></label>
                                <textarea id='evento-motivo' rows={2} className={inputCls} value={form.motivo}
                                    onChange={(e) => set('motivo', e.target.value)}
                                    placeholder='Ex.: Reajuste anual IPCA conforme cláusula 9ª' />
                            </div>

                            {erro && (
                                <p role='alert' className='flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'>
                                    <BiErrorCircle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
                                    <span>{erro}</span>
                                </p>
                            )}

                            <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
                                <button type='button' onClick={() => { setForm(null); setErro(''); }}
                                    className={`rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 ${focoCls}`}>
                                    Cancelar
                                </button>
                                <button type='button' onClick={lancar} disabled={lancando || !form.motivo.trim() || (form.tipo === 'renovacao' && !form.data_fim_nova) || (form.tipo === 'cancelamento' && !form.data_evento)}
                                    className={`rounded-lg bg-vox-blue-900 px-4 py-2 text-sm font-semibold text-white hover:bg-vox-blue-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-vox-blue-600 dark:hover:bg-vox-blue-500 ${focoCls}`}>
                                    {lancando ? 'Lançando…' : form.tipo === 'cancelamento' ? 'Cancelar contrato' : form.tipo === 'reativacao' ? 'Reverter cancelamento' : 'Lançar evento'}
                                </button>
                            </div>
                            {/* faixa da cor do tipo (último filho: o space-y não a desloca) */}
                            <span aria-hidden='true' className={`absolute inset-y-0 left-0 !mt-0 w-1 ${corForm.ponto}`} />
                        </section>
                    )}

                    {itens === null ? <LoadingSpinner /> : itens.length === 0 ? (
                        <p className='text-gray-500 dark:text-gray-400'>Nenhum evento lançado neste contrato.</p>
                    ) : (
                        <div>
                            <ol className='relative'>
                                {itens.map((item, i) => {
                                    const proximo = itens[i + 1];
                                    return (
                                        <li key={item.chave} className='relative flex gap-3 sm:gap-4'>
                                            <div className={`hidden w-24 shrink-0 pt-0.5 text-right text-sm font-semibold tabular-nums sm:block ${item.substituida ? 'text-gray-400 line-through dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                                                {item.pendente ? '' : item.dataFmt}
                                            </div>
                                            <div className='relative flex w-5 shrink-0 justify-center'>
                                                {proximo && (
                                                    <span aria-hidden='true'
                                                        className={`absolute left-1/2 -bottom-3 -translate-x-1/2 border-l-2 ${item.pendente ? (isGerente ? 'top-8' : 'top-6') : 'top-3'} ${proximo.fase === 'implantacao'
                                                            ? 'border-dashed border-gray-300 dark:border-gray-600'
                                                            : 'border-solid border-vox-blue-300 dark:border-vox-blue-700'}`} />
                                                )}
                                                <Ponto item={item} comBotao={isGerente} />
                                            </div>

                                            {item.pendente ? (
                                                <div className={`min-w-0 flex-1 ${proximo ? 'pb-6' : ''}`}>
                                                    <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-vox-green-300 bg-vox-green-50 px-4 py-3 dark:border-vox-green-800 dark:bg-vox-green-900/20'>
                                                        <p className='text-sm font-medium text-vox-green-900 dark:text-vox-green-100'>
                                                            Contrato em implantação — ainda não fatura.
                                                        </p>
                                                        {isGerente && (
                                                            <button type='button' onClick={() => setAtivar({ modo: 'ativar' })}
                                                                className={`${botaoAtivarCls} px-4 py-2 text-sm`}>
                                                                <BiPlay className='h-4 w-4' aria-hidden='true' />
                                                                Ativar contrato
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={`min-w-0 flex-1 ${proximo ? 'pb-6' : ''} ${item.substituida ? 'opacity-70' : ''}`}>
                                                    <p className='text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400 sm:hidden'>{item.dataFmt}</p>
                                                    <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
                                                        <h4 className={`font-semibold ${item.tipo === 'cadastro' ? 'text-gray-600 dark:text-gray-300' : 'text-gray-900 dark:text-white'}`}>
                                                            {item.titulo}
                                                        </h4>
                                                        {item.ehAtivacao && podeCorrigir && isGerente && (
                                                            <button type='button'
                                                                onClick={() => setAtivar({ modo: 'corrigir', inicial: { data: item.dataFmt, responsavel_cliente: item.responsavelCliente } })}
                                                                className={`inline-flex items-center gap-1 rounded text-sm font-medium text-sky-700 hover:underline dark:text-sky-300 ${focoCls}`}>
                                                                <BiCalendarEdit className='h-4 w-4' aria-hidden='true' />
                                                                Corrigir data
                                                            </button>
                                                        )}
                                                    </div>
                                                    {item.substituida && (
                                                        <p className='text-xs italic text-gray-500 dark:text-gray-400'>Data substituída por uma correção posterior.</p>
                                                    )}
                                                    {item.linhas.map((l, k) => (
                                                        <p key={k} className='mt-0.5 whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-300 tabular-nums'>{l}</p>
                                                    ))}
                                                    {item.lancamento && (
                                                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 tabular-nums'>{item.lancamento}</p>
                                                    )}
                                                    {item.id && item.anexoNome && (
                                                        <button type='button' onClick={() => baixarTermo(item)}
                                                            className={`mt-1 inline-flex items-center gap-1 rounded text-sm font-medium text-vox-blue-700 hover:underline dark:text-vox-blue-300 ${focoCls}`}>
                                                            <BiDownload className='h-4 w-4' aria-hidden='true' />Termo: {item.anexoNome}
                                                        </button>
                                                    )}
                                                    {item.id && !item.anexoNome && item.tipo !== 'homologacao' && isGerente && (
                                                        <label className='mt-1 inline-flex cursor-pointer items-center gap-1 rounded text-sm font-medium text-gray-600 hover:underline focus-within:ring-2 focus-within:ring-vox-blue-500 dark:text-gray-300'>
                                                            <BiPaperclip className='h-4 w-4' aria-hidden='true' />Anexar termo
                                                            <input type='file' accept='application/pdf' className='sr-only' aria-label={`Anexar termo ao evento ${item.titulo}`}
                                                                onChange={(e) => anexarPelaTimeline(item.id, e)} />
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                            {itens.length > 1 && (
                                <p className='mt-5 flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400'>
                                    {temTrilhoContinuo && (
                                        <span className='inline-flex items-center gap-2'>
                                            <span aria-hidden='true' className='h-0 w-6 border-t-2 border-solid border-vox-blue-300 dark:border-vox-blue-700' />faturando
                                        </span>
                                    )}
                                    {temTrilhoTracejado && (
                                        <span className='inline-flex items-center gap-2'>
                                            <span aria-hidden='true' className='h-0 w-6 border-t-2 border-dashed border-gray-300 dark:border-gray-600' />em implantação
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <footer className='flex justify-end border-t border-gray-200 dark:border-gray-700 px-6 py-3'>
                    <button type='button' onClick={onClose}
                        className={`rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 ${focoCls}`}>
                        Fechar
                    </button>
                </footer>
            </div>

            {ativar && (
                <AtivarContrato contrato={{ ...contrato, status }} client={client} modo={ativar.modo} inicial={ativar.inicial}
                    onClose={() => setAtivar(null)} onAtivado={aoAtivar} />
            )}
        </div>
    );
};

export default EventosContrato;
